import { AssistantToolName, AssistantUITools } from "@repo/api/client";
import { Button, Flex, Icon, Text, type IconName } from "@repo/ui";
import { formatTimestampRelative } from "@repo/utils/dateUtils";
import { match } from "@repo/utils/utils";
import { getToolName, ToolUIPart } from "ai";
import { useState } from "react";

type AssistantToolUIPart<
	ToolName extends AssistantToolName = AssistantToolName,
> = ToolUIPart<Pick<AssistantUITools, ToolName>>;

export const toolsDisplay: {
	[ToolName in AssistantToolName]: {
		loadingText: string;
		completedText: (result: AssistantUITools[ToolName]["output"]) => string;
		icon: IconName;
	};
} = {
	search_healthcare_providers: {
		loadingText: "Searching for healthcare providers...",
		completedText: (result) => {
			if (!result) {
				return "Search failed";
			}
			// Refusals explain themselves — a Demo search with no phones
			// configured is fixed from the control bar, not by retrying.
			if (!result.success) {
				return result.message;
			}
			const total = result.total || 0;
			const displayed = result.providers?.length || 0;
			if (total === 0) {
				return "No providers found";
			}
			if (total === 1) {
				return "Found 1 provider";
			}
			if (total === displayed) {
				return `Found ${total} providers`;
			}
			return `Loaded ${displayed} of ${total} found providers`;
		},
		icon: "Search",
	},
	call_provider: {
		loadingText: "Calling provider...",
		completedText: (result) => {
			if (!result.success) {
				return match(result.code, {
					REJECTED_BY_USER: "Phone call request rejected",
					INPUT_VALIDATION_ERROR: result.message,
					OUTSIDE_BUSINESS_HOURS: result.message,
					USER_ACTION_REQUIRED: result.message,
					INTERNAL_ERROR: result.message,
					FORBIDDEN: result.message,
				});
			}
			return "Phone call in progress";
		},
		icon: "Phone",
	},
	submit_referral_prior_authorization: {
		loadingText: "Submitting prior authorization...",
		completedText: (result) => {
			if (!result.success) {
				return result.code === "REJECTED_BY_USER" ||
					result.code === "INPUT_VALIDATION_ERROR"
					? result.code === "REJECTED_BY_USER"
						? "Prior authorization request rejected"
						: result.message
					: result.message;
			}
			return "Prior authorization in progress";
		},
		icon: "FileText",
	},

	send_message_to_patient: {
		loadingText: "Sending message...",
		completedText: (result) => {
			if (!result.success) {
				if (result.code === "CONSENT_REQUEST_IN_FLIGHT") {
					return `A consent request was already sent to the patient ${formatTimestampRelative(new Date(result.lastRequestedAt))}. Waiting for the patient's consent before sending a message.`;
				}
				const text = match(result.code, {
					CONSENT_DECLINED:
						"Consent declined by patient to receive messages with PHI",
					REJECTED_BY_USER: "Send message request rejected",
					INPUT_VALIDATION_ERROR: result.message,
					OUTSIDE_BUSINESS_HOURS: result.message,
					JOB_ALREADY_RUNNING:
						"Already sending a message to this patient. Please wait for it to finish before sending another message.",
					MESSAGE_NOT_ALLOWED: result.message,
					NOTIFICATION_SKIPPED: result.message,
					USER_ACTION_REQUIRED: result.message,
					INTERNAL_ERROR: result.message,
					FORBIDDEN: result.message,
				});
				// Fallback to passing through the message for any unforeseen codes
				return text ?? (result as any).message ?? "Message failed";
			}
			if (result.consentStatus === "requested") {
				return "Consent request sent to patient";
			}
			return "Message being sent";
		},
		icon: "BotMessageSquare",
	},
	attach_records_send_referral: {
		loadingText: "Sending referral...",
		completedText: (result) => {
			if (!result.success) {
				return result.code === "REJECTED_BY_USER" ||
					result.code === "INPUT_VALIDATION_ERROR"
					? result.code === "REJECTED_BY_USER"
						? "Referral sending rejected"
						: result.message
					: result.message;
			}
			return "Referral being sent";
		},
		icon: "FileText",
	},
	check_patient_eligibility: {
		loadingText: "Checking eligibility...",
		completedText: (result) => {
			if (!result.success) {
				return result.code === "REJECTED_BY_USER" ||
					result.code === "INPUT_VALIDATION_ERROR"
					? result.code === "REJECTED_BY_USER"
						? "Eligibility check rejected"
						: result.message
					: result.message;
			}
			return "Eligibility check in progress";
		},
		icon: "ShieldCheck",
	},
	// update_provider_info tool has been disabled
};

export function ToolCallView({ part }: { part: AssistantToolUIPart }) {
	const [showTechnicalDetails, setShowTechnicalDetails] = useState(false);

	const toolCall = getToolName(part) as AssistantToolName;

	// The cast above is unchecked: a tool missing from toolsDisplay would throw
	// on destructure and blank the page, so fall back to generic text.
	const display = toolsDisplay[toolCall] as
		| (typeof toolsDisplay)[AssistantToolName]
		| undefined;
	const { completedText, icon, loadingText } = display ?? {
		loadingText: "Working…",
		completedText: () => "Done",
		icon: "Bot" as const,
	};

	const isLoading =
		part.state === "input-available" || part.state === "input-streaming";
	// output-error carries no output: the model's tool input failed validation,
	// so the tool never ran. completedText reads `result.success` and would
	// throw on undefined, blanking the page.
	const text =
		part.state === "output-error"
			? "Couldn't run this step, so nothing was sent. Try asking again."
			: isLoading
				? loadingText
				: completedText(part.output as any);

	// Check if this is an error state
	const isError =
		part.state === "output-error" ||
		(part.state === "output-available" &&
			part.output != null &&
			"success" in part.output &&
			part.output.success === false);

	return (
		<Flex
			column
			gap="md"
			padding="md"
			borderRadius="md"
			background="sand2"
			borderWidth="md"
			borderColor="sandA3"
			flex
		>
			<Flex align="center" gap="xs" justify="space-between">
				<Flex align="center" gap="xs">
					<Icon
						name={icon}
						size="sm"
						color={
							part.state === "input-available"
								? "blue9"
								: isError
									? "red10"
									: part.state === "output-available"
										? "green10"
										: "sand9"
						}
						shrink={0}
						style={
							isLoading ? { animation: "spin 1s linear infinite" } : undefined
						}
					/>
					<Text size="sm" color="sand12" weight="medium">
						{text}
					</Text>
				</Flex>
				<Button
					size="sm"
					variant="ghost"
					onPress={() => setShowTechnicalDetails(!showTechnicalDetails)}
				>
					<Flex align="center" gap="xs">
						<Icon name="ChevronDown" size="xs" />
						<Text size="sm" color="sand11">
							{showTechnicalDetails ? "Hide" : "Show"} technical details
						</Text>
					</Flex>
				</Button>
			</Flex>

			{/* Technical details - collapsible */}
			{showTechnicalDetails && (
				<Flex column gap="xs">
					{part.input && (
						<Flex column gap="xs">
							<Flex align="center" justify="space-between">
								<Text size="sm" color="sand10" weight="medium">
									Parameters:
								</Text>
								<Button
									size="sm"
									variant="ghost"
									onPress={() =>
										navigator.clipboard.writeText(
											JSON.stringify(part.input, null, 2),
										)
									}
								>
									<Icon name="Copy" size="xs" />
								</Button>
							</Flex>
							<Flex padding="xs">
								<Text
									size="xs"
									color="sand11"
									style={{
										whiteSpace: "pre-wrap",
										fontFamily: "monospace",
									}}
								>
									{JSON.stringify(part.input, null, 2)}
								</Text>
							</Flex>
						</Flex>
					)}
					{part.state === "output-available" && part.output && (
						<Flex column gap="xs">
							<Flex align="center" justify="space-between">
								<Text size="sm" color="sand10" weight="medium">
									Raw Result:
								</Text>
								<Button
									size="sm"
									variant="ghost"
									onPress={() =>
										navigator.clipboard.writeText(
											JSON.stringify(part.output, null, 2),
										)
									}
								>
									<Icon name="Copy" size="xs" />
								</Button>
							</Flex>
							<Flex padding="xs">
								<Text
									size="xs"
									color="sand11"
									style={{
										whiteSpace: "pre-wrap",
										fontFamily: "monospace",
									}}
								>
									{JSON.stringify(part.output, null, 2)}
								</Text>
							</Flex>
						</Flex>
					)}
					{part.state === "output-error" && (
						<Flex column gap="xs">
							<Flex align="center" justify="space-between">
								<Text size="sm" color="sand10" weight="medium">
									Raw Result:
								</Text>
								<Button
									size="sm"
									variant="ghost"
									onPress={() => navigator.clipboard.writeText(part.errorText)}
								>
									<Icon name="Copy" size="xs" />
								</Button>
							</Flex>
							<Flex padding="xs">
								<Text
									size="xs"
									color="sand11"
									style={{
										whiteSpace: "pre-wrap",
										fontFamily: "monospace",
									}}
								>
									Error: {part.errorText}
								</Text>
							</Flex>
						</Flex>
					)}
				</Flex>
			)}
		</Flex>
	);
}
